import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Typography,
  Box,
  Chip
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import * as api from '../../api/api';

const FUNCTION_OPTIONS = [
  'Developer', 'Designer', 'Product Manager', 'Quality Assurance', 
  'Business Analyst', 'Data Analyst', 'DevOps Engineer', 'Other'
];

const DEVELOPER_FOCUS_OPTIONS = [
  'Frontend', 'Backend', 'Full Stack', 'Mobile', 'Data Engineering'
];


function AddMemberModal({ 
  open, 
  onClose, 
  onMemberAdded, 
  editingMember = null,
  readOnly = false 
}) {
    const [form, setForm] = useState({
    name: '',
    company: 'CA',
    location: '',
    function: '',
    focus: '',
    department: '',
    cost_center: '',
    role: '',
    startDate: null,
    endDate: null,
    tags: []
  });
    const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  
    // Rate data for location and role options
  const [rateData, setRateData] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [filteredLocationOptions, setFilteredLocationOptions] = useState([]);
  const [filteredRoleOptions, setFilteredRoleOptions] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Tags data
  const [availableTags, setAvailableTags] = useState([]);
  const [newTagDialogOpen, setNewTagDialogOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');

  // Departments data
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [costCenterOptions, setCostCenterOptions] = useState([]);

    // Load rate data and tags on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch rates
        const ratesResponse = await api.getRates();
        const ratesData = ratesResponse.rates ? ratesResponse : await ratesResponse.json();
        const rates = ratesData.rates || [];
        setRateData(rates);

        // Extract unique companies
        const companies = [...new Set(rates.map(rate => String(rate.company || '')).filter(c => c))].sort();
        setCompanyOptions(companies);

        // Fetch tags
        const tagsResponse = await api.getTags();
        const tagsData = tagsResponse.tags || tagsResponse || [];
        setAvailableTags(Array.isArray(tagsData) ? tagsData : []);

        // Fetch departments
        const deptsResponse = await api.getDepartments();
        const deptsData = deptsResponse.departments || deptsResponse || [];
        setAvailableDepartments(Array.isArray(deptsData) ? deptsData : []);

        setDataLoaded(true);
        console.log('Data loaded for AddMemberModal:', { 
          rates: rates.length, 
          companies: companies.length,
          tags: tagsData.length || 0
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        // Fallback: Set default options if data fails to load
        setCompanyOptions(['CA', 'Contractor']);
        setAvailableTags([]);
        setDataLoaded(true);
      }
    };

    if (open) {
      fetchData();
    }
  }, [open]);

      // Helper function to normalize function values
  const normalizeFunctionValue = (functionValue) => {
    if (!functionValue) return '';
    
    // Map common variations to standard values
    const functionMap = {
      'BA': 'Business Analyst',
      'Business Analyst': 'Business Analyst',
      'Dev': 'Developer',
      'Developer': 'Developer',
      'QA': 'Quality Assurance',
      'Quality Assurance': 'Quality Assurance',
      'PM': 'Product Manager',
      'Product Manager': 'Product Manager',
      'Designer': 'Designer',
      'Data Analyst': 'Data Analyst',
      'DevOps Engineer': 'DevOps Engineer',
      'Other': 'Other'
    };
    
    return functionMap[functionValue] || 'Other';
  };

  // Set default company and populate form when modal opens
  useEffect(() => {
    if (open && dataLoaded) {
      if (editingMember) {
        // Populate form with existing member data
        const normalizedFunction = normalizeFunctionValue(editingMember.function);
        
        setForm({
          name: editingMember.name || '',
          company: editingMember.company || 'CA',
          location: editingMember.location || '',
          function: normalizedFunction,
          focus: editingMember.focus || '',
          department: editingMember.department || '',
          cost_center: editingMember.cost_center || '',
          role: editingMember.role || '',
          startDate: editingMember.startDate ? dayjs(editingMember.startDate) : null,
          endDate: editingMember.endDate ? dayjs(editingMember.endDate) : null,
          tags: Array.isArray(editingMember.tags) ? editingMember.tags : []
        });
      } else if (companyOptions.length > 0 && !form.company) {
        // Set default company for new members
        setForm(prev => ({ 
          ...prev, 
          company: companyOptions.includes('CA') ? 'CA' : companyOptions[0],
          tags: []
        }));
      }
    }
  }, [open, companyOptions, editingMember, dataLoaded]);

    // Helper function to get display name for location code
  const getLocationDisplayName = (locationCode) => {
    const locationMap = {
      'US': 'US',
      'UK': 'UK', 
      'IND': 'India',
      'CR': 'Costa Rica',
      'BR': 'Brazil',
      'CO': 'Colombia',
      'MX': 'Mexico'
    };
    return locationMap[locationCode] || locationCode;
  };

  // Update location options when company changes
  useEffect(() => {
    if (form.company && rateData.length > 0) {
      const locationCodes = [...new Set(
        rateData
          .filter(rate => rate.company === form.company)
          .map(rate => String(rate.location || ''))
          .filter(l => l)
      )].sort();
      
      // Create location objects with code and display name
      const locations = locationCodes.map(code => ({
        code: code,
        display: getLocationDisplayName(code)
      }));
      
      setFilteredLocationOptions(locations);
      
            // Auto-select the first location if none is selected (but not when editing)
      if (!form.location && locations.length > 0 && !editingMember) {
        setForm(prev => ({ 
          ...prev, 
          location: locations[0].code,
          role: '' 
        }));
      }
      // Reset location if not in the filtered list (but not when editing)
      else if (form.location && !locationCodes.includes(form.location) && !editingMember) {
        setForm(prev => ({ 
          ...prev, 
          location: locations.length > 0 ? locations[0].code : '',
          role: '' 
        }));
      }
    }
  }, [form.company, rateData, editingMember]);

  // Update role options when company and location change
  useEffect(() => {
    if (form.company && form.location && rateData.length > 0) {
      const roles = [...new Set(
        rateData
          .filter(rate => 
            rate.company === form.company && 
            rate.location === form.location
          )
          .map(rate => String(rate.role || ''))
          .filter(r => r)
      )].sort();
      setFilteredRoleOptions(roles);
      
            // Reset role if not in the filtered list (but not when editing existing member)
      if (form.role && !roles.includes(form.role) && !editingMember) {
        setForm(prev => ({ ...prev, role: '' }));
      }
    }
  }, [form.company, form.location, rateData, editingMember]);

  // Update cost center options when department changes
  useEffect(() => {
    const dept = availableDepartments.find(d => d.name === form.department);
    setCostCenterOptions(dept?.cost_centers || []);
    // Clear cost_center if it no longer belongs to the selected dept
    if (form.cost_center && dept) {
      const stillValid = (dept.cost_centers || []).some(cc => cc.name === form.cost_center);
      if (!stillValid) setForm(prev => ({ ...prev, cost_center: '' }));
    }
  }, [form.department, availableDepartments]);

  // Tag management functions
  const handleAddNewTag = async () => {
    if (!newTagName.trim()) {
      setError('Tag name is required');
      return;
    }

    try {
      const response = await api.createTag({ name: newTagName.trim() });
      const newTag = response.tag || response;
      
      // Add to available tags
      setAvailableTags(prev => [...prev, newTag]);
      
      // Add to form tags
      setForm(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.id]
      }));
      
      // Reset and close dialog
      setNewTagName('');
      setNewTagDialogOpen(false);
      setError('');
      
    } catch (error) {
      console.error('Error creating tag:', error);
      setError('Failed to create tag');
    }
  };

  const handleRemoveTag = (tagId) => {
    setForm(prev => ({
      ...prev,
      tags: prev.tags.filter(id => id !== tagId)
    }));
  };

  

  

    const handleSave = async () => {
    // Validate required fields
    if (!form.name || !form.company || !form.location || !form.function || !form.role) {
      setError('Name, Company, Location, Function, and Role are required');
      return;
    }

    // Validate Developer-specific requirements
    if (form.function === 'Developer' && !form.focus) {
      setError('Focus is required for Developer function');
      return;
    }

    // Validate date range if both dates are provided
    if (form.startDate && form.endDate && form.endDate.isBefore(form.startDate)) {
      setError('End date must be after start date');
      return;
    }

    try {
      setSaving(true);
      setError('');

                  const memberData = {
        name: form.name,
        company: form.company,
        location: form.location,
        function: form.function,
        focus: form.function === 'Developer' ? form.focus : undefined,
        department: form.department || undefined,
        cost_center: form.cost_center || undefined,
        role: form.role,
        startDate: form.startDate ? form.startDate.format('YYYY-MM-DD') : undefined,
        endDate: form.endDate ? form.endDate.format('YYYY-MM-DD') : undefined,
        tags: form.tags
      };
      
            console.log('AddMemberModal: Saving member with tags:', {
        memberName: memberData.name,
        tags: memberData.tags,
        tagCount: memberData.tags.length,
        fullMemberData: memberData
      });

            let result;
      if (editingMember) {
        result = await api.updateITRosterMember(editingMember.id, memberData);
        console.log('AddMemberModal: Update member API response:', result);
        setSuccess('Team member updated successfully!');
      } else {
        result = await api.addITRosterMember(memberData);
        console.log('AddMemberModal: Add member API response:', result);
        setSuccess('Team member added successfully!');
      }
      
                        // Check if tags were properly saved
      if (result && result.tags) {
        console.log('AddMemberModal: Tags successfully saved to member:', result.tags);
      } else if (memberData.tags && memberData.tags.length > 0) {
        console.warn('AddMemberModal: Tags were sent but not returned in response.');
        console.log('AddMemberModal: Full API response:', result);
        console.log('AddMemberModal: Response structure:', Object.keys(result || {}));
        console.log('AddMemberModal: Original tags sent:', memberData.tags);
        
        // The tags should now be saved by the backend, even if not returned in response
        console.log('AddMemberModal: Tags should be saved by backend even if not in response');
      }
      
      // Call the callback to refresh data
      if (onMemberAdded) {
        await onMemberAdded(result);
      }

      // Clear success message after 2 seconds
      setTimeout(() => {
        setSuccess('');
        handleClose();
      }, 2000);

    } catch (error) {
      console.error('Error saving team member:', error);
      setError(`Failed to ${editingMember ? 'update' : 'add'} team member. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

    const handleClose = () => {
    if (saving) return; // Prevent closing while saving
    
        setForm({
      name: '',
      company: companyOptions.includes('CA') ? 'CA' : (companyOptions[0] || 'CA'),
      location: '',
      function: '',
      focus: '',
      department: '',
      cost_center: '',
      role: '',
      startDate: null,
      endDate: null,
      tags: []
    });
    setError('');
    setSuccess('');
    onClose();
  };

    return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMember ? 'Edit Team Member' : 'Add Team Member'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <TextField
          label="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          fullWidth
          sx={{ mb: 2, mt: 1 }}
          disabled={readOnly || saving}
          required
        />
        
        <FormControl fullWidth sx={{ mb: 2 }} required>
          <InputLabel>Company</InputLabel>
          <Select
            value={form.company}
            label="Company"
            onChange={(e) => setForm({ ...form, company: e.target.value })}
            disabled={readOnly || saving}
          >
            {companyOptions.map(company => (
              <MenuItem key={company} value={company}>
                {String(company)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }} required>
          <InputLabel>Location</InputLabel>
          <Select
            value={form.location}
            label="Location"
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            disabled={readOnly || saving}
          >
                        {filteredLocationOptions.map(location => (
              <MenuItem key={location.code} value={location.code}>
                {location.display}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }} required>
          <InputLabel>Function</InputLabel>
          <Select
            value={form.function}
            label="Function"
            onChange={(e) => setForm({ ...form, function: e.target.value })}
            disabled={readOnly || saving}
          >
            {FUNCTION_OPTIONS.map(func => (
              <MenuItem key={func} value={func}>
                {String(func)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

                {form.function === 'Developer' && (
          <FormControl fullWidth sx={{ mb: 2 }} required>
            <InputLabel>Focus</InputLabel>
            <Select
              value={form.focus}
              label="Focus"
              onChange={(e) => setForm({ ...form, focus: e.target.value })}
              disabled={readOnly || saving}
            >
              {DEVELOPER_FOCUS_OPTIONS.map(focus => (
                <MenuItem key={focus} value={focus}>
                  {String(focus)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Department</InputLabel>
          <Select
            value={form.department}
            label="Department"
            onChange={(e) => setForm({ ...form, department: e.target.value, cost_center: '' })}
            disabled={readOnly || saving}
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {availableDepartments.map(dept => (
              <MenuItem key={dept.id} value={dept.name}>
                {dept.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }} disabled={readOnly || saving || costCenterOptions.length === 0}>
          <InputLabel>Cost Center</InputLabel>
          <Select
            value={form.cost_center}
            label="Cost Center"
            onChange={(e) => setForm({ ...form, cost_center: e.target.value })}
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {costCenterOptions.map(cc => (
              <MenuItem key={cc.id} value={cc.name}>
                {cc.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

                                <FormControl fullWidth sx={{ mb: 2 }} required>
          <InputLabel>Role</InputLabel>
          <Select
            value={form.role}
            label="Role"
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            disabled={readOnly || saving}
          >
            {filteredRoleOptions.map(role => (
              <MenuItem key={role} value={role}>
                {String(role)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

                        {/* Tags Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Tags</InputLabel>
          <Select
            multiple
            value={form.tags}
            label="Tags"
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
            disabled={readOnly || saving}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected
                  .filter(value => value !== '__add_new__')
                  .map((tagId) => {
                    const tag = availableTags.find(t => t.id === tagId);
                    return (
                      <Chip
                        key={tagId}
                        label={tag?.name || tagId}
                        size="small"
                        sx={{
                          bgcolor: (t) => t.palette.mode === 'dark' ? '#3a3a3a' : '#e0e0e0',
                          color: (t) => t.palette.mode === 'dark' ? '#d0d0d0' : '#424242',
                          '&:hover': { bgcolor: (t) => t.palette.mode === 'dark' ? '#4a4a4a' : '#d5d5d5' }
                        }}
                        variant="filled"
                                                  onDelete={(e) => {
                            e && e.stopPropagation && e.stopPropagation();
                            handleRemoveTag(tagId);
                          }}
                        deleteIcon={<Box sx={{ fontSize: '0.8rem' }}>×</Box>}
                      />
                    );
                  })}
              </Box>
            )}
          >
            {/* Add New Tag Option */}
                          <MenuItem 
                onClick={(e) => {
                  e && e.stopPropagation && e.stopPropagation();
                  setNewTagDialogOpen(true);
                }}
              sx={{ 
                borderBottom: '1px solid',
                borderColor: 'divider',
                mb: 1,
                fontStyle: 'italic',
                color: 'grey.600'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddIcon sx={{ fontSize: '1rem' }} />
                <Typography>Add New Tag...</Typography>
              </Box>
            </MenuItem>
            
            {/* Existing Tags */}
            {availableTags.map((tag) => (
              <MenuItem key={tag.id} value={tag.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={tag.name}
                    size="small"
                    sx={{
                      bgcolor: (t) => t.palette.mode === 'dark' ? '#3a3a3a' : '#e0e0e0',
                      color: (t) => t.palette.mode === 'dark' ? '#d0d0d0' : '#424242',
                      fontSize: '0.7rem',
                      height: 20
                    }}
                    variant="filled"
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <DatePicker
            label="Start Date (Optional)"
            value={form.startDate}
            onChange={(newValue) => setForm({ ...form, startDate: newValue })}
            disabled={readOnly || saving}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'medium'
              }
            }}
          />
          <DatePicker
            label="End Date (Optional)"
            value={form.endDate}
            onChange={(newValue) => setForm({ ...form, endDate: newValue })}
            disabled={readOnly || saving}
            minDate={form.startDate || undefined}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'medium'
              }
            }}
          />
        </Box>

        
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
                <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={readOnly || saving}
        >
          {saving ? (editingMember ? 'Updating...' : 'Adding...') : (editingMember ? 'Update Member' : 'Add Member')}
        </Button>
                        </DialogActions>
      </Dialog>
      
      {/* Add New Tag Dialog */}
      <Dialog open={newTagDialogOpen} onClose={() => setNewTagDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add New Tag</DialogTitle>
        <DialogContent>
          <TextField
            label="Tag Name"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            fullWidth
            margin="normal"
            required
            disabled={saving}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTagDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleAddNewTag} variant="contained" disabled={saving}>
            Add Tag
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

export default AddMemberModal;